import _ from "lodash";
import { AppCard } from "@lib/components/AppComponents";
import { useIntl } from "react-intl";
import { ActionButton } from "../../Buttons";
import { Empty, Skeleton, Typography } from "antd";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { getContrastColor, randomColor } from "@lib/helpers/ColorHelper";
import { shortenString } from "@lib/helpers/StringHelper";
import { StyledFlex } from "../../Table/index.styled";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const TftSalesCallsAgentChart = (props: Props) => {
  const { query, extra, navigate, loading, refetch } = props;
  const { mode } = useTheme();
  const { data, isLoading, isFetching } = query || {};
  const { messages } = useIntl();

  const agentData = useMemo(() => {
    const groupByAIModelCount = data?.data?.groupByAIModelCount || [];
    return groupByAIModelCount
      .map((item) => ({
        agentName: item.ai_model_name || "Unknown",
        count: item.count,
      }))
      .sort((a, b) => b.count - a.count); // Sort descending by count
  }, [data]);

  const agents = useMemo(
    () => agentData.map((item) => item.agentName),
    [agentData],
  );

  const labelText = (agentName: string) => {
    const item = agentData.find((a) => a.agentName === agentName);
    return `${shortenString(agentName, 20, true)}: ${item?.count || 0}`;
  };

  const chartSeries: ApexOptions["series"] = useMemo(() => {
    const series: ApexOptions["series"] = [
      {
        data: agentData.map((item) => item.count),
      },
    ];

    return series;
  }, [agentData]);

  const chartOptions = useMemo(() => {
    return {
      theme: {
        mode: mode,
      },
      chart: {
        type: "bar",
        height: 400,
        width: 400,
      },
      plotOptions: {
        bar: {
          barHeight: "100%",
          distributed: true,
          horizontal: true,
          dataLabels: {
            position: "bottom",
          },
        },
      },
      colors: agents.map((v, i) => {
        return randomColor(i);
      }),
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: agents.map((v, i) => {
            return getContrastColor(randomColor(i));
          }),
        },
        formatter: function (val: number, opt: any) {
          return labelText(agents[opt.dataPointIndex]);
        },
        offsetX: 0,
        dropShadow: {
          enabled: true,
        },
      },
      tooltip: {
        custom: function ({ dataPointIndex }: any) {
          return `<div style="padding: 10px;">
            <div>${labelText(agents[dataPointIndex])}</div>
          </div>`;
        },
        shared: true,
        intersect: false,
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      xaxis: {
        categories: agents,
        labels: {
          show: true,
          formatter: (val: number) => {
            return `${val}`;
          },
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
    } as ApexOptions;
  }, [agents, mode, agentData]);

  return (
    <AppCard
      title={messages["common.callsByAgentChart"] as string}
      extra={
        <StyledFlex style={{ gap: 10 }}>
          {extra}
          <>
            {navigate && (
              <Typography.Link onClick={() => navigate()}>
                {messages["common.viewAll"] as string}
              </Typography.Link>
            )}
          </>
          {refetch && (
            <ActionButton
              type="refresh"
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              loading={loading}
            />
          )}
        </StyledFlex>
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {agentData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartOptions.chart?.type}
              height={chartOptions.chart?.height || 400}
            />
          )}
        </>
      )}
    </AppCard>
  );
};

export default TftSalesCallsAgentChart;
